// src/dateDiffInWords.js
var dateDiffInWords_default = dateDiffInWords = function(seconds) {
  let days = Math.floor(seconds / 86400);
  let hours = Math.floor((seconds - days * 86400) / 3600);
  let minutes = Math.floor((seconds - days * 86400 - hours * 3600) / 60);
  let parts = [];
  if (days > 0) {
    parts.push(`${days} days`);
  }
  if (hours > 0) {
    parts.push(`${hours} hours`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} minutes`);
  }
  return parts.join(", ");
};

// src/dateTimeFormat.js
var dateTimeFormat_default = dateTimeFormat = function({ dateStyle = "full", timeStyle = "short" } = {}) {
  return Intl.DateTimeFormat(
    Intl.DateTimeFormat().resolvedOptions().locale,
    { dateStyle, timeStyle }
  );
};

// src/hooks/NPD/useIsDetractor.jsx
var import_react = require("react");

// src/NPD.js
var NPD_default = NPD = {};
NPD.isDetractor = (value, asNPS) => {
  if (asNPS) {
    return value >= -1 && value < 0.4;
  } else {
    return value >= -1 && value <= -0.2;
  }
};
NPD.isNeutral = (value, asNPS) => {
  if (asNPS) {
    return value >= 0.4 && value <= 0.6;
  } else {
    return value > -0.2 && value <= 0.2;
  }
};
NPD.isPromoter = (value, asNPS) => {
  if (asNPS) {
    return value > 0.6;
  } else {
    return value > 0.2;
  }
};
NPD.typeName = (value, asNPS) => {
  if (NPD.isDetractor(value, asNPS)) {
    return "detractor";
  }
  if (NPD.isNeutral(value, asNPS)) {
    return "neutral";
  }
  if (NPD.isPromoter(value, asNPS)) {
    return "promoter";
  }
  return void 0;
};
NPD.countsFromBuckets = (bucketCounts, asNPS) => {
  bucketCounts ||= {};
  let countsByGroups = { detractors: 0, neutrals: 0, promoters: 0, total: bucketCounts.count || 0 };
  Object.keys(bucketCounts).forEach((key) => {
    if (NPD.isDetractor(key, asNPS)) {
      countsByGroups.detractors += bucketCounts[key];
    } else if (NPD.isNeutral(key, asNPS)) {
      countsByGroups.neutrals += bucketCounts[key];
    } else if (NPD.isPromoter(key, asNPS)) {
      countsByGroups.promoters += bucketCounts[key];
    }
    countsByGroups.total += bucketCounts[key];
  });
  return countsByGroups;
};

// src/hooks/NPD/useIsDetractor.jsx
var useIsDetractor_default = useIsDetractor = (asNPS) => {
  return (0, import_react.useCallback)((value) => NPD_default.isDetractor(value, asNPS), [asNPS]);
};

// src/hooks/NPD/useIsNeutral.jsx
var import_react2 = require("react");
var useIsNeutral_default = useIsNeutral = (asNPS) => {
  return (0, import_react2.useCallback)((value) => NPD_default.isNeutral(value, asNPS), [asNPS]);
};

// src/hooks/NPD/useIsPromoter.jsx
var import_react3 = require("react");
var useIsPromoter_default = useIsPromoter = (asNPS) => {
  return (0, import_react3.useCallback)((value) => NPD_default.isPromoter(value, asNPS), [asNPS]);
};

// src/hooks/useCSSVar.jsx
var import_react4 = require("react");
var useCSSVar_default = useCSSVar = (refOrElem, varName) => {
  const cssValue = (0, import_react4.useMemo)(() => {
    const elem = refOrElem?.hasOwnProperty("current") ? refOrElem.current : refOrElem;
    try {
      if (elem) {
        return window.getComputedStyle(elem).getPropertyValue(varName).trim();
      } else if (elem == void 0) {
        return window.getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
      } else {
        return void 0;
      }
    } catch (error) {
      console.log(`Unable to obtain value for CSS variable ${varName} from element`, elem);
      console.warn(error);
      return void 0;
    }
  }, [refOrElem, varName]);
  return cssValue;
};

// src/hooks/useItemLoader.jsx
var import_react5 = require("react");
var import_dequal = require("dequal");
var useItemLoader_default = useItemLoader = ({ type, id, getFunc, opts = {}, canQuery = true }) => {
  const [requestState, setRequestState] = (0, import_react5.useState)(useItemLoader.UNSET);
  const [item, setItem] = (0, import_react5.useState)({});
  const [previousId, setPreviousId] = (0, import_react5.useState)();
  const [previousOpts, setPreviousOpts] = (0, import_react5.useState)();
  const [needsLoad, setNeedsLoad] = (0, import_react5.useState)(false);
  const reload = () => setNeedsLoad(true);
  const loadItem = (requestedOpts) => {
    setRequestState(useItemLoader.LOADING);
    setNeedsLoad(false);
    const promise = getFunc ? getFunc(id, opts) : type?.getById(id, opts);
    promise.then((found) => {
      if ((0, import_dequal.dequal)(opts, requestedOpts)) {
        setItem((cur) => (0, import_dequal.dequal)(cur, found) ? cur : found);
        setRequestState(useItemLoader.LOADED);
      }
    }).catch((e) => {
      console.warn(e);
      setRequestState(useItemLoader.FAILED);
    });
  };
  (0, import_react5.useEffect)(() => {
    if (!(0, import_dequal.dequal)(opts, previousOpts)) {
      setPreviousOpts(opts);
      setNeedsLoad(true);
    }
  }, [opts, previousOpts]);
  (0, import_react5.useEffect)(() => {
    if (!(0, import_dequal.dequal)(id, previousId)) {
      setPreviousId(id);
      setNeedsLoad(true);
    }
  }, [opts, previousOpts]);
  (0, import_react5.useEffect)(() => {
    if (needsLoad && canQuery) {
      setItem(void 0);
      loadItem(opts);
    }
  }, [needsLoad, canQuery]);
  return { requestState, item, reload };
};
useItemLoader.UNSET = "unset";
useItemLoader.LOADING = "loading";
useItemLoader.FAILED = "failed";
useItemLoader.LOADED = "loaded";

// src/hooks/useListLoader.jsx
var import_react6 = require("react");
var import_dequal2 = require("dequal");
var useListLoader_default = useListLoader = ({ type, filters = {}, opts = {}, listFunc, canQuery = true }) => {
  const [requestState, setRequestState] = (0, import_react6.useState)(useListLoader.UNSET);
  const [itemsById, setItemsById] = (0, import_react6.useState)({});
  const [previousFilters, setPreviousFilters] = (0, import_react6.useState)();
  const [previousOpts, setPreviousOpts] = (0, import_react6.useState)();
  const [needsLoad, setNeedsLoad] = (0, import_react6.useState)(false);
  const [totalCount, setTotalCount] = (0, import_react6.useState)(void 0);
  const itemList = (0, import_react6.useMemo)(() => Object.values(itemsById || {}), [itemsById]);
  const reload = () => setNeedsLoad(true);
  const loadItems = (requestedFilters, requestedOpts, page = opts?.page || 1) => {
    setRequestState(useListLoader.LOADING);
    setNeedsLoad(false);
    if (page != 1 && opts?.include && void 0 === totalCount) {
      opts.include = [opts.include].flatMap((v) => v).filter((v) => v != "counts");
      if (opts.include.length == 0) {
        delete opts.include;
      }
    }
    const args = { filters: requestedFilters, ...opts, page };
    const promise = listFunc ? listFunc(args) : type?.list(args);
    promise.then((items) => {
      if ((0, import_dequal2.dequal)(filters, requestedFilters)) {
        const byId = {};
        items.forEach((item) => byId[item.id] = item);
        setItemsById((cur) => ({ ...cur, ...byId }));
        if (items.meta?.isLastPage) {
          setRequestState(useListLoader.LOADED);
        } else {
          loadItems(requestedFilters, requestedOpts, page + 1);
        }
        if (items.meta?.totalCount) {
          setTotalCount(items.meta?.totalCount);
        }
      }
    }).catch((e) => {
      console.warn(e);
      setRequestState(useListLoader.FAILED);
    });
  };
  (0, import_react6.useEffect)(() => {
    if (!(0, import_dequal2.dequal)(filters, previousFilters)) {
      setPreviousFilters(filters);
      setNeedsLoad(true);
    }
  }, [filters, previousFilters]);
  (0, import_react6.useEffect)(() => {
    if (!(0, import_dequal2.dequal)(opts, previousOpts)) {
      setPreviousOpts(opts);
      setNeedsLoad(true);
    }
  }, [opts, previousOpts]);
  (0, import_react6.useEffect)(() => {
    if (needsLoad && canQuery) {
      setItemsById({});
      setTotalCount(void 0);
      loadItems(filters, opts);
    }
  }, [needsLoad, canQuery]);
  return { requestState, itemList, itemsById, reload, filters, totalCount };
};
useListLoader.UNSET = "unset";
useListLoader.LOADING = "loading";
useListLoader.FAILED = "failed";
useListLoader.LOADED = "loaded";

// src/hooks/useScrolledTo.jsx
var import_react7 = require("react");
var elementIsVisibleInViewport = (el, partiallyVisible = true) => {
  const { top, left, bottom, right } = el.getBoundingClientRect();
  const { innerHeight, innerWidth } = window;
  return partiallyVisible ? (top > 0 && top < innerHeight || bottom > 0 && bottom < innerHeight) && (left > 0 && left < innerWidth || right > 0 && right < innerWidth) : top >= 0 && left >= 0 && bottom <= innerHeight && right <= innerWidth;
};
var useScrolledTo_default = useScrolledTo = (ref) => {
  const [isVisible, setIsVisible] = (0, import_react7.useState)(false);
  const [viewed, setViewed] = (0, import_react7.useState)(false);
  const handleIntersection = (entries, _observer) => {
    const intersecting = !!entries[0]?.isIntersecting;
    setIsVisible((cur) => cur == intersecting ? cur : intersecting);
  };
  (0, import_react7.useEffect)(() => {
    if (isVisible && !viewed) {
      setViewed(true);
    }
  }, [isVisible]);
  (0, import_react7.useEffect)(() => {
    console.log("ref updated", ref);
    if (ref?.current) {
      const observer = new IntersectionObserver(handleIntersection);
      observer.observe(ref.current);
      const intersecting = elementIsVisibleInViewport(ref.current);
      setViewed(intersecting);
      setIsVisible(intersecting);
      return () => observer.disconnect();
    }
  }, [ref]);
  return { isVisible, viewed };
};

// src/Rounding.js
var Rounding_default = Rounding = function() {
  if (Number.EPSILON === void 0) {
    Number.EPSILON = Math.pow(2, -52);
  }
  if (Math.trunc === void 0) {
    Math.trunc = function(v) {
      return v < 0 ? Math.ceil(v) : Math.floor(v);
    };
  }
  const powers = [
    1,
    10,
    100,
    1e3,
    1e4,
    1e5,
    1e6,
    1e7,
    1e8,
    1e9,
    1e10,
    1e11,
    1e12,
    1e13,
    1e14,
    1e15,
    1e16,
    1e17,
    1e18,
    1e19,
    1e20,
    1e21,
    1e22
  ];
  const intpow10 = function(power) {
    if (power < 0 || power > 22) {
      return Math.pow(10, power);
    }
    return powers[power];
  };
  const isRound = function(num, decimalPlaces) {
    const p = intpow10(decimalPlaces);
    return Math.round(num * p) / p === num;
  };
  const decimalAdjust = function(type, num, decimalPlaces) {
    if (type !== "round" && isRound(num, decimalPlaces || 0))
      return num;
    const p = intpow10(decimalPlaces || 0);
    const n = num * p * (1 + Number.EPSILON);
    return Math[type](n) / p;
  };
  return {
    round: function(num, decimalPlaces) {
      return decimalAdjust("round", num, decimalPlaces);
    },
    ceil: function(num, decimalPlaces) {
      return decimalAdjust("ceil", num, decimalPlaces);
    },
    floor: function(num, decimalPlaces) {
      return decimalAdjust("floor", num, decimalPlaces);
    },
    trunc: function(num, decimalPlaces) {
      return decimalAdjust("trunc", num, decimalPlaces);
    },
    toFixed: function(num, decimalPlaces) {
      return decimalAdjust("round", num, decimalPlaces).toFixed(decimalPlaces);
    }
  };
}();

// src/main.js
module.exports = {
  useIsDetractor: useIsDetractor_default,
  useIsNeutral: useIsNeutral_default,
  useIsPromoter: useIsPromoter_default,
  useCSSVar: useCSSVar_default,
  useListLoader: useListLoader_default,
  useItemLoader: useItemLoader_default,
  useScrolledTo: useScrolledTo_default,
  dateDiffInWords: dateDiffInWords_default,
  dateTimeFormat: dateTimeFormat_default,
  NPD: NPD_default,
  Rounding: Rounding_default
};
//# sourceMappingURL=bundle.js.map
